import { useEffect, useId, useRef } from 'react';
import { focusInitialElement, trapTabKey } from './focusTrap';

/**
 * Right-side drawer. Same focus lifecycle as Modal.
 */
export function Drawer({ open, onClose, title, description, children, footer }) {
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement;
    const frame = window.requestAnimationFrame(() => {
      focusInitialElement(panelRef.current, { current: closeRef.current });
    });

    function onKey(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      trapTabKey(event, panelRef.current);
    }

    document.addEventListener('keydown', onKey);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKey);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex justify-end">
      <button
        type="button"
        aria-hidden
        tabIndex={-1}
        onClick={() => onCloseRef.current?.()}
        className="absolute inset-0 cursor-default bg-eorig-navy/40 backdrop-blur-[1px]"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className="relative z-10 flex h-full w-full max-w-lg flex-col border-l border-eorig-border bg-white shadow-eorig-card outline-none"
      >
        <div className="flex items-start justify-between gap-3 border-b border-eorig-border px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-sm font-semibold text-eorig-ink">
              {title}
            </h2>
            {description ? (
              <p id={descriptionId} className="mt-1 text-[12px] leading-snug text-eorig-muted">
                {description}
              </p>
            ) : null}
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={() => onCloseRef.current?.()}
            className="rounded-full border border-eorig-border px-3 py-1 text-[12px] font-semibold text-eorig-ink hover:bg-eorig-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eorig-cyan/35"
          >
            Close
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer ? (
          <div className="border-t border-eorig-border bg-eorig-surface px-5 py-3">{footer}</div>
        ) : null}
      </div>
    </div>
  );
}
